import { useEffect, useState } from "react";
import api, { money } from "../../lib/api";
import { PageHeader, StatCard } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Undo2, RefreshCw } from "lucide-react";

export default function AdminFinance() {
  const [invoices, setInvoices] = useState([]);
  const [pol, setPol] = useState([]);
  const [refunds, setRefunds] = useState({ items: [], total_completed: 0, total_reversed: 0 });
  const [busy, setBusy] = useState(null);

  const load = () => Promise.all([
    api.get("/invoices"),
    api.get("/insurance"),
    api.get("/admin/refunds"),
  ]).then(([i, p, r]) => {
    setInvoices(i.data);
    setPol(p.data);
    setRefunds(r.data);
  });
  useEffect(() => { load(); }, []);

  const financed = invoices.filter(i => i.financed);
  const deployed = financed.reduce((s, i) => s + i.amount * 0.97, 0);
  const fees = financed.reduce((s, i) => s + i.amount * 0.035, 0);
  const premium = pol.reduce((s, p) => s + p.premium, 0);

  const reverse = async (rid) => {
    if (!window.confirm("Reverse this refund? The buyer's wallet will be debited and the seller's wallet credited.")) return;
    setBusy(rid);
    try {
      await api.post(`/admin/refunds/${rid}/reverse`);
      toast.success("Refund reversed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Reverse failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader title="Finance Portfolio" subtitle="Capital deployed, factoring fees, insurance premium collected, and seller refunds"/>
      <div className="p-8 space-y-6">
        <div className="grid md:grid-cols-4 gap-4">
          <StatCard label="Capital deployed" value={money(deployed)} tone="ink"/>
          <StatCard label="Factoring fees" value={money(fees)} hint="3.5% per advance"/>
          <StatCard label="Insurance premium" value={money(premium)} hint="10–20% commission"/>
          <StatCard label="Platform revenue" value={money(fees + premium*0.15)} hint="Net to verqo"/>
        </div>

        {/* Refunds ledger */}
        <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="admin-refunds">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="display text-lg">Seller → buyer refunds</div>
              <div className="text-[11px] text-foreground/55 mt-0.5 mono">
                Completed {money(refunds.total_completed)} · Reversed {money(refunds.total_reversed)}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={load} className="h-9 rounded-full" data-testid="admin-refunds-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Refresh
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-widest text-foreground/60">
              <tr>
                <th className="px-5 py-3">Ref</th>
                <th className="px-5 py-3">Seller</th>
                <th className="px-5 py-3">Buyer</th>
                <th className="px-5 py-3">Invoice</th>
                <th className="px-5 py-3">Amount</th>
                <th className="px-5 py-3">Reason</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {refunds.items.map(r => (
                <tr key={r.id} data-testid={`admin-refund-${r.ref}`}>
                  <td className="px-5 py-3 mono text-xs">{r.ref}</td>
                  <td className="px-5 py-3">{r.seller_name}</td>
                  <td className="px-5 py-3">{r.buyer_name}</td>
                  <td className="px-5 py-3 mono text-xs">{r.invoice_code}</td>
                  <td className="px-5 py-3 display">{money(r.amount)}</td>
                  <td className="px-5 py-3 text-foreground/70">{r.reason_label}</td>
                  <td className="px-5 py-3">
                    <span className={`chip ${
                      r.status === "completed" ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {r.status === "completed" && (
                      <Button size="sm" variant="outline"
                        disabled={busy === r.id}
                        onClick={() => reverse(r.id)}
                        data-testid={`reverse-${r.ref}`}
                        className="rounded-full">
                        <Undo2 className="w-3.5 h-3.5 mr-1"/>{busy === r.id ? "Reversing…" : "Reverse"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {refunds.items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-foreground/60">No refunds issued yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Financed invoices (existing) */}
        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <div className="px-5 py-3 border-b border-border display text-lg">Financed invoices</div>
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-xs uppercase tracking-widest text-foreground/60">
              <tr><th className="px-5 py-3">Invoice</th><th className="px-5 py-3">Buyer</th><th className="px-5 py-3">Seller</th><th className="px-5 py-3">Amount</th><th className="px-5 py-3">Payout</th><th className="px-5 py-3">Fee</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {financed.map(i => (
                <tr key={i.id}>
                  <td className="px-5 py-4 mono">{i.code}</td>
                  <td className="px-5 py-4">{i.buyer_name}</td>
                  <td className="px-5 py-4">{i.seller_name}</td>
                  <td className="px-5 py-4 display">{money(i.amount)}</td>
                  <td className="px-5 py-4">{money(i.amount*0.97)}</td>
                  <td className="px-5 py-4 text-accent-foreground">{money(i.amount*0.035)}</td>
                </tr>
              ))}
              {financed.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-foreground/60">No financed invoices yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
